import { useMutation } from '@apollo/client';
import { Button, Fields } from '@ets-global/b2c-website-ui';
import { Form, Formik } from 'formik';
import { instanceOf } from 'prop-types';
import React, { useContext, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { UPDATE_USER_PROFILE } from '../../../graphql/mutations';
import { USER } from '../../../graphql/queries';
import errorsHandler from '../../../helpers/errorsHandler';
import { getTranslatedUserCivility } from '../../../helpers/Formatter/UserFormatter';
import { useNotification } from '../../../hooks';
import useYup from '../../../hooks/useYup';
import { AuthContext } from '../../../store/Auth';
import AccountBlock from '../../Account/Block';
import ErrorMessage from '../../Base/ErrorMessage/ErrorMessage';
import FormikObserver from '../../FormikObserver';

const UpdateProfile = ({ civilities }) => {
  const { user } = useContext(AuthContext);
  const { newNotification } = useNotification();
  const { t } = useTranslation();
  const [isFormUpdated, setFormUpdated] = useState(false);
  const { Yup } = useYup();

  const [updateProfile] = useMutation(UPDATE_USER_PROFILE, {
    refetchQueries: [{ query: USER }],
  });

  const onSubmit = async (data, actions) => {
    if (compareUserAndData(data, user)) {
      actions.setSubmitting(false);

      return;
    }

    try {
      const { civilityId, firstname, lastname, email, phoneNumber } = data;
      await updateProfile({
        variables: {
          userProfile: { civilityId, firstname, lastname, email, phoneNumber },
        },
      });
      data.civility = data.civilityId;

      newNotification({
        content: t('user-account.update-profile.message.updated'),
        type: 'success',
      });
      setFormUpdated(false);
    } catch (error) {
      errorsHandler({ error, actions, newNotification, t });
    }

    actions.setSubmitting(false);
  };

  const compareUserAndData = (data, user) => {
    return (
      data['civilityId'] === user['civility'] &&
      data['firstname'] === user['firstname'] &&
      data['lastname'] === user['lastname'] &&
      data['email'] === user['email']
    );
  };

  civilities = civilities.map(({ id }) => ({
    value: id,
    label: getTranslatedUserCivility(id, t),
  }));
  civilities.unshift({ value: '', label: '' });

  let validations = {
    civilityId: Yup.string().required(),
    firstname: Yup.string().required(),
    lastname: Yup.string().required(),
    email: Yup.string().required().email(),
  };
  const UserSchema = Yup.object().shape(validations);

  return (
    <AccountBlock title={t('user-account.update-profile.title')} className={'account-infos'}>
      <Formik initialValues={{ civilityId: user.civility, ...user }} validationSchema={UserSchema} onSubmit={onSubmit}>
        {({ isSubmitting, errors: { form }, values }) => (
          <Form className="update-profile__form" data-cypress="user-form">
            {form && <ErrorMessage>{form}</ErrorMessage>}
            <Fields.FormikFastField
              id={'civilityId'}
              name={'civilityId'}
              component={Fields.SelectField}
              label={t('user-account.update-profile.form.label.civility')}
              autoComplete={'honorific-prefix'}
              required={true}
              options={civilities}
            />
            <Fields.FormikFastField
              id={'firstname'}
              name={'firstname'}
              component={Fields.TextField}
              label={t('user-account.update-profile.form.label.firstname')}
              autoComplete={'given-name'}
              required={true}
            />
            <Fields.FormikFastField
              id={'lastname'}
              name={'lastname'}
              component={Fields.TextField}
              label={t('user-account.update-profile.form.label.lastname')}
              autoComplete={'family-name'}
              required={true}
            />
            <Fields.FormikFastField
              id={'email'}
              name={'email'}
              component={Fields.TextField}
              label={t('user-account.update-profile.form.label.email')}
              type={'email'}
              required={true}
              autoComplete={'email'}
            />
            <Button
              aria-label={`${t('user-account.update-profile.form.label.save')} button`}
              className={'form__submit'}
              disabled={!isFormUpdated}
              loading={isSubmitting}
              type="submit"
            >
              <Trans>user-account.update-profile.form.label.save</Trans>
            </Button>
            <FormikObserver values={values} onChange={() => setFormUpdated(true)} />
          </Form>
        )}
      </Formik>
    </AccountBlock>
  );
};

UpdateProfile.propTypes = {
  civilities: instanceOf(Object).isRequired,
};

export default UpdateProfile;
